import React from 'react';
import { Box, Text, Grid, GridItem } from '@chakra-ui/react';
import { getIntensityColor } from 'src/theme';
import { useCurrency } from 'src/hooks/useCurrency';
import { attachCurrency } from 'src/utils/currency';

export type WeeklySpendHeatmapData = {
  day: string;
  category1: number;
  category2: number;
  category3: number;
};

export type WeeklySpendHeatmapProps = {
  data: WeeklySpendHeatmapData[];
  isLoading?: boolean;
  isError?: boolean;
};

const formatValue = (value: number, currencyCode: string) => {
  return attachCurrency(`${value}K`, currencyCode);
};

export const WeeklySpendHeatmap: React.FC<WeeklySpendHeatmapProps> = ({
  data,
  isLoading = false,
  isError = false,
}) => {
  const { code: currencyCode } = useCurrency();
  if (isLoading) {
    return (
      <Box
        height="200px"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Text>Loading...</Text>
      </Box>
    );
  }

  if (isError || data.length === 0) {
    return (
      <Box
        height="200px"
        display="flex"
        alignItems="center"
        justifyContent="center">
        <Text>No data available</Text>
      </Box>
    );
  }

  const categories = ['category1', 'category2', 'category3'] as const;
  const days = data.map((item) => item.day);

  return (
    <Box height="200px" width="100%">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={4}>
        <Text fontSize="lg" fontWeight="semibold">
          Weekly Spend Heatmap (Sample View)
        </Text>
        <Box display="flex" alignItems="center" gap={2}>
          <Text fontSize="sm" color="content.muted" mr={2}>
            Spend Intensity
          </Text>
          {[1.5, 3.2, 4.4, 6.2, 9.2].map((value, index) => (
            <Box
              key={index}
              w="4"
              h="4"
              bg={getIntensityColor(value)}
              borderRadius="sm"
            />
          ))}
        </Box>
      </Box>

      <Grid
        templateColumns="repeat(7, 1fr)"
        gap={1}
        templateRows="repeat(4, 1fr)">
        {days.map((day) => (
          <GridItem key={day}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              height="30px">
              <Text fontSize="sm" fontWeight="medium" color="content.label">
                {day}
              </Text>
            </Box>
          </GridItem>
        ))}

        {categories.map((category) => (
          <React.Fragment key={category}>
            {data.map((dayData, dayIndex) => {
              const value = dayData[category];
              const backgroundColor = getIntensityColor(value);
              return (
                <GridItem key={`${category}-${dayIndex}`}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="40px"
                    bg={backgroundColor}
                    borderRadius="sm"
                    border="1px solid"
                    borderColor="surface.border"
                    _hover={{
                      transform: 'scale(1.05)',
                      transition: 'transform 0.2s',
                    }}>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="white"
                      textShadow="0 1px 2px rgba(0,0,0,0.5)">
                      {formatValue(value, currencyCode)}
                    </Text>
                  </Box>
                </GridItem>
              );
            })}
          </React.Fragment>
        ))}
      </Grid>
    </Box>
  );
};
